import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bike, 
  Plus, 
  Pencil, 
  Trash2, 
  Phone, 
  User,
  ToggleLeft,
  ToggleRight,
  Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DeliveryDriversContent = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Erro ao carregar entregadores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    
    if (!formData.phone.trim() || formData.phone.replace(/\D/g, '').length < 10) {
      toast.error('Telefone inválido');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingDriver) {
        await axios.put(`${API}/delivery-drivers/${editingDriver.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Entregador atualizado!');
      } else {
        await axios.post(`${API}/delivery-drivers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Entregador cadastrado!');
      }
      
      setShowModal(false);
      setEditingDriver(null);
      setFormData({ name: '', phone: '', active: true });
      fetchDrivers();
    } catch (error) {
      console.error('Error saving driver:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar entregador');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      phone: driver.phone,
      active: driver.active
    });
    setShowModal(true);
  };

  const handleDelete = async (driver) => {
    if (!window.confirm(`Remover o entregador ${driver.name}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/delivery-drivers/${driver.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entregador removido');
      fetchDrivers();
    } catch (error) {
      toast.error('Erro ao remover entregador');
    }
  };

  const toggleActive = async (driver) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/delivery-drivers/${driver.id}`, {
        active: !driver.active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(driver.active ? 'Entregador desativado' : 'Entregador ativado');
      fetchDrivers();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const formatPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `(${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    if (cleaned.length === 12) {
      return `(${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
    }
    return phone;
  };

  const openNewModal = () => {
    setEditingDriver(null);
    setFormData({ name: '', phone: '', active: true });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Bike className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Entregadores</h1>
            <p className="text-gray-400 text-sm">Gerencie sua equipe de entrega</p>
          </div>
        </div>
        <button
          onClick={openNewModal}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all"
          data-testid="add-driver-btn"
        >
          <Plus className="w-4 h-4" />
          Novo Entregador
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
        <p className="text-purple-300 text-sm">
          <strong>Como funciona:</strong> Quando você marcar um pedido como "Saiu para Entrega", 
          {drivers.length === 0 && " nenhuma notificação será enviada (cadastre entregadores para ativar)."}
          {drivers.length === 1 && ` o entregador ${drivers[0].name} receberá automaticamente os detalhes no WhatsApp.`}
          {drivers.length > 1 && " você poderá escolher qual entregador fará a entrega e ele receberá os detalhes no WhatsApp."}
        </p>
      </div>

      {/* Lista de Entregadores */}
      {drivers.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
          <Bike className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">Nenhum entregador cadastrado</p>
          <button
            onClick={openNewModal}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all"
          >
            Cadastrar Primeiro Entregador
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {drivers.map((driver) => (
            <div 
              key={driver.id}
              className={`bg-gray-800/50 border rounded-lg p-4 transition-all ${
                driver.active ? 'border-gray-700 hover:border-purple-500/50' : 'border-gray-800 opacity-60'
              }`}
              data-testid={`driver-card-${driver.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${driver.active ? 'bg-purple-500/20' : 'bg-gray-700'}`}>
                    <User className={`w-5 h-5 ${driver.active ? 'text-purple-400' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{driver.name}</h3>
                    <div className="flex items-center gap-1 text-gray-400 text-sm">
                      <Phone className="w-3 h-3" />
                      {formatPhone(driver.phone)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => toggleActive(driver)}
                  className="text-gray-400 hover:text-white transition-colors"
                  title={driver.active ? 'Desativar' : 'Ativar'}
                >
                  {driver.active ? (
                    <ToggleRight className="w-6 h-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-6 h-6" />
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                <div className="flex items-center gap-1 text-gray-500 text-xs">
                  <Package className="w-3 h-3" />
                  {driver.total_deliveries || 0} entregas
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(driver)}
                    className="p-1.5 text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded transition-all"
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(driver)}
                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                    title="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700 shadow-xl">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white">
                {editingDriver ? 'Editar Entregador' : 'Novo Entregador'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: João Silva"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  data-testid="driver-name-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  WhatsApp *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(38) 99999-9999"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  data-testid="driver-phone-input"
                />
                <p className="text-gray-500 text-xs mt-1">
                  O entregador receberá os detalhes da entrega neste número
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                />
                <label htmlFor="active" className="text-gray-300 text-sm">
                  Entregador ativo
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingDriver(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-driver-btn"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      {editingDriver ? 'Salvar' : 'Cadastrar'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryDriversContent;
